import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import prettier from 'prettier/standalone';
import * as markdownPlugin from 'prettier/plugins/markdown';

const previewClasses = `
  [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:mb-4 [&_h1]:mt-6 [&_h1]:border-b [&_h1]:border-stone-200 [&_h1]:pb-2
  [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:mb-3 [&_h2]:mt-5 [&_h2]:border-b [&_h2]:border-stone-200 [&_h2]:pb-1
  [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mb-2 [&_h3]:mt-4
  [&_h4]:text-lg [&_h4]:font-medium [&_h4]:mb-2 [&_h4]:mt-3
  [&_p]:mb-3 [&_p]:leading-relaxed [&_p]:text-gray-800
  [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:mb-3
  [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:mb-3
  [&_li]:mb-1
  [&_blockquote]:border-l-4 [&_blockquote]:border-stone-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-600 [&_blockquote]:mb-3
  [&_code]:bg-amber-100 [&_code]:text-stone-800 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:font-mono [&_code]:text-sm
  [&_pre]:bg-stone-800 [&_pre]:text-stone-100 [&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_pre]:mb-3
  [&_pre_code]:bg-transparent [&_pre_code]:text-stone-100 [&_pre_code]:p-0
  [&_a]:text-amber-700 [&_a]:underline
  [&_table]:border-collapse [&_table]:mb-3 [&_table]:w-full
  [&_th]:border [&_th]:border-stone-300 [&_th]:bg-stone-100 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left
  [&_td]:border [&_td]:border-stone-300 [&_td]:px-3 [&_td]:py-2
  [&_hr]:border-stone-200 [&_hr]:my-6
  [&_img]:max-w-full [&_img]:rounded-lg
`;

const MarkdownFormatter = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState('');
  const [viewMode, setViewMode] = useState('preview');
  const [error, setError] = useState('');
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);

  const sampleMarkdown = `#    Project Documentation

## Overview
This is a **sample markdown** document with *various* formatting elements.

##   Features
-   Live preview rendering
-  Markdown beautification
- Syntax cleanup


### Code Example
\`\`\`javascript
function greet(name) {
  return "Hello, " + name + "!";
}
\`\`\`

> **Note:** This is a blockquote with important information.

|  Column 1 | Column 2   |
|---|---|
| Value A | Value B |
|  Value C  |   Value D |

1.  First item
2.   Second item
3. Third item

Check out [this link](https://example.com) and use \`inline code\` too.

---

Final paragraph with a horizontal rule above.`;

  const renderedHTML = useMemo(() => {
    if (!input.trim()) return '';
    try {
      return DOMPurify.sanitize(marked.parse(input));
    } catch (err) {
      return `<p style="color:red">Error rendering markdown: ${err.message}</p>`;
    }
  }, [input]);

  const beautifyMarkdown = async () => {
    if (!input.trim()) {
      setError('Please enter some Markdown content');
      setOutput('');
      return;
    }
    try {
      const formatted = await prettier.format(input, {
        parser: 'markdown',
        plugins: [markdownPlugin],
        proseWrap: 'preserve'
      });
      setOutput(formatted);
      setMode('beautify');
      setViewMode('text');
      setError('');
    } catch (err) {
      setError('Failed to beautify: ' + err.message);
      setOutput('');
    }
  };

  const loadSample = () => {
    setInput(sampleMarkdown);
    setOutput('');
    setMode('');
    setError('');
    setViewMode('preview');
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setMode('');
    setError('');
    setViewMode('preview');
  };

  const FullScreenModal = ({ isOpen, onClose, content, copyText }) => {
    if (!isOpen) return null;
    const copyToClipboard = () => navigator.clipboard.writeText(copyText);
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg w-full h-[90vh] flex flex-col">
          <div className="flex justify-between items-center p-4 border-b">
            <h3 className="text-lg font-medium">Full Screen View</h3>
            <div className="flex gap-2">
              {copyText && (
                <button
                  data-testid="markdown-fullscreen-copy-button"
                  onClick={copyToClipboard}
                  className="px-3 py-1 text-sm bg-stone-500 text-white rounded hover:bg-stone-600 transition-colors shadow-sm"
                >
                  Copy
                </button>
              )}
              <button
                data-testid="markdown-fullscreen-close-button"
                onClick={onClose}
                className="px-3 py-1 text-sm bg-stone-600 text-white rounded hover:bg-stone-700 transition-colors shadow-sm"
              >
                Close (ESC)
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto p-4">{content}</div>
        </div>
      </div>
    );
  };

  React.useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') setIsFullScreenOpen(false);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="py-4 px-4">
        <div className="w-full px-4 mx-auto">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200">
            <span className="mr-2">←</span>
            Back to Tools
          </Link>
        </div>
      </div>

      <div className="flex-1 px-4 py-8">
        <div className="w-full px-4 mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-light text-gray-800 mb-2">Markdown Formatter</h1>
            <p className="text-gray-600 text-sm">Live preview and beautify Markdown documents</p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-8">
            <button data-testid="markdown-beautify-button" onClick={beautifyMarkdown} className="px-4 py-2 bg-stone-500 text-white rounded-lg hover:bg-stone-600 transition-colors duration-200 text-sm font-medium shadow-sm">Beautify</button>
            <button data-testid="markdown-load-sample-button" onClick={loadSample} className="px-4 py-2 bg-stone-400 text-white rounded-lg hover:bg-stone-500 transition-colors duration-200 text-sm font-medium shadow-sm">Load Sample</button>
            <button data-testid="markdown-clear-all-button" onClick={clearAll} className="px-4 py-2 bg-stone-600 text-white rounded-lg hover:bg-stone-700 transition-colors duration-200 text-sm font-medium shadow-sm">Clear All</button>
          </div>

          {error && (
            <div className="text-center mb-6">
              <p data-testid="markdown-error-message" className="text-red-600 text-sm bg-red-50 inline-block px-4 py-2 rounded-lg">{error}</p>
            </div>
          )}

          <div className="flex gap-6">
            <div className="w-1/4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Input</label>
                <textarea
                  data-testid="markdown-input-textarea"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Enter your Markdown here..."
                  className="w-full h-[calc(100vh-320px)] px-4 py-3 border border-amber-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-transparent resize-none font-mono text-sm bg-amber-50"
                />
              </div>
              <div className="text-center">
                <p className="text-gray-500 text-xs">{input.length} characters</p>
              </div>
            </div>

            <div className="flex-1 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Output
                    {viewMode === 'preview' && input.trim() && (
                      <span className="ml-2 inline-block bg-stone-100 text-stone-800 text-xs px-2 py-1 rounded-full">Live Preview</span>
                    )}
                    {viewMode === 'text' && mode === 'beautify' && (
                      <span className="ml-2 inline-block bg-stone-100 text-stone-800 text-xs px-2 py-1 rounded-full">Beautified</span>
                    )}
                  </label>

                  <div className="flex items-center space-x-2">
                    {viewMode === 'text' && (
                      <button
                        data-testid="markdown-preview-view-button"
                        onClick={() => setViewMode('preview')}
                        className="px-3 py-1 rounded text-xs font-medium bg-stone-100 text-gray-600 hover:text-gray-900 transition-all duration-200"
                      >
                        Back to Preview
                      </button>
                    )}

                    {(input.trim() || output) && (
                      <button
                        data-testid="markdown-expand-button"
                        onClick={() => setIsFullScreenOpen(true)}
                        className="px-3 py-1 text-xs bg-stone-600 text-white rounded hover:bg-stone-700 transition-colors"
                        title="Expand to full screen"
                      >
                        🔍 Expand
                      </button>
                    )}
                  </div>
                </div>

                {viewMode === 'preview' ? (
                  <div
                    data-testid="markdown-preview-pane"
                    className={`md-preview w-full h-[calc(100vh-320px)] px-6 py-4 border border-stone-200 rounded-lg shadow-sm bg-stone-50 overflow-auto ${previewClasses}`}
                  >
                    {input.trim() ? (
                      <div dangerouslySetInnerHTML={{ __html: renderedHTML }} />
                    ) : (
                      <p className="text-gray-400 text-sm">Rendered preview will appear here as you type...</p>
                    )}
                  </div>
                ) : (
                  <textarea
                    data-testid="markdown-output-textarea"
                    value={output}
                    readOnly
                    placeholder="Beautified Markdown will appear here... (click Beautify)"
                    className="w-full h-[calc(100vh-320px)] px-4 py-3 border border-stone-200 rounded-lg shadow-sm bg-stone-50 resize-none font-mono text-sm whitespace-pre-wrap"
                  />
                )}
              </div>
              <div className="text-center">
                <p className="text-gray-500 text-xs">
                  {viewMode === 'text' ? `${output.length} characters` : `${input.trim() ? 'Rendering live' : '0 characters'}`}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FullScreenModal
        isOpen={isFullScreenOpen}
        onClose={() => setIsFullScreenOpen(false)}
        content={
          viewMode === 'preview' ? (
            <div className={`md-preview ${previewClasses}`} dangerouslySetInnerHTML={{ __html: renderedHTML }} />
          ) : (
            <pre className="font-mono text-sm whitespace-pre-wrap">{output}</pre>
          )
        }
        copyText={viewMode === 'preview' ? input : output}
      />
    </div>
  );
};

export default MarkdownFormatter;
